import type {
  PayrollEmployeeData,
  PayrollTimeEntry,
  PayrollBreakdown,
} from "@/lib/types/reports";
import { entryHours, type ReportTimeEntryRow } from "@/lib/reports/time-entry-fields";
import type { PayrollUserMeta } from "./payroll-types";

/**
 * Group denormalized, codegen-typed time-entry rows into per-employee payroll
 * data. Employee identity/rate comes from `userMeta` (joined from `app_users`);
 * client/ticket labels come from the row's denormalized columns — no nested
 * relations, no `unwrapRelation`.
 */
export function groupPayrollEntries(
  data: ReportTimeEntryRow[],
  userMeta: Map<string, PayrollUserMeta>
): PayrollEmployeeData[] {
  const employeeMap = new Map<string, PayrollEmployeeData>();

  data?.forEach((entry) => {
    const userId = entry.user_id;
    if (!userId) return;
    const meta = userMeta.get(userId);

    if (!employeeMap.has(userId)) {
      employeeMap.set(userId, {
        id: userId,
        name: meta?.name || entry.user_name || "Unknown User",
        email: meta?.email || "",
        hourlyRate: meta?.hourlyRate,
        totalHours: 0,
        totalPay: undefined,
        entries: [],
        byProject: {},
        byTicket: {},
        byClient: {},
      });
    }

    const employee = employeeMap.get(userId)!;
    const hours = entryHours(entry);
    const pay = employee.hourlyRate ? hours * employee.hourlyRate : undefined;
    employee.totalHours += hours;
    if (employee.hourlyRate && pay) employee.totalPay = (employee.totalPay || 0) + pay;

    const clientName =
      (entry.company_name ?? entry.contact_name ?? "").trim() || "Unknown";
    const clientKey = entry.company_id ?? "unknown";

    const entryRecord: PayrollTimeEntry = {
      id: entry.id,
      date: entry.date_worked,
      hours,
      description: entry.description ?? "",
      ticketId: entry.ticket_id ?? undefined,
      ticketTitle: entry.ticket_title || "Unknown Ticket",
      projectId: entry.company_id ?? undefined,
      projectName: clientName,
      clientId: entry.company_id ?? undefined,
      clientName,
      pay,
    };

    employee.entries.push(entryRecord);
    // byClient: grouped by company; byTicket: grouped by ticket. byProject is a
    // back-compat alias of byClient (no separate "project" entity exists yet).
    addPayrollGroup(employee.byClient, clientKey, employee, hours, pay, entryRecord);
    addPayrollGroup(employee.byProject, clientKey, employee, hours, pay, entryRecord);
    addPayrollGroup(
      employee.byTicket,
      entry.ticket_id || "unknown",
      employee,
      hours,
      pay,
      entryRecord
    );
  });

  return Array.from(employeeMap.values()).sort((a, b) => a.name.localeCompare(b.name));
}

function addPayrollGroup(
  groupMap: Record<string, PayrollBreakdown>,
  key: string,
  employee: PayrollEmployeeData,
  hours: number,
  pay: number | undefined,
  entry: PayrollTimeEntry
) {
  if (!groupMap[key]) {
    groupMap[key] = { hours: 0, pay: undefined, entries: [] };
  }

  groupMap[key].hours += hours;
  if (employee.hourlyRate && pay) groupMap[key].pay = (groupMap[key].pay || 0) + pay;
  groupMap[key].entries.push(entry);
}
